import React, { lazy, Suspense, useContext, useState } from 'react';
import { func, object } from 'prop-types';
import { connect } from 'react-redux';
import { getContext } from '@fiverr-private/fiverr_context';
import { s_share } from '@fiverr-private/fit/icons';
import pathfinder from '@fiverr-private/pathfinder';
import { EventTypes } from '../../../../../shared/utils/events/types';
import { CollectionActionType } from '../../../../../shared/types/collectionActionType';
import eventsReporter from '../../../../../shared/utils/events/reporter';
import { OwnerType } from '../../../../../shared/types/ownerType';
import { redirectTo } from '../../../../../shared/utils/redirect';
import { updateCollection, socialShareCollection } from '../../../store/collection/actions';
import { deleteCollection } from '../../../utils/api/collections';
import ActionIcon from '../../../../../shared/components/ActionIcon';
import Menu from '../../../../../shared/components/Menu';
import SharedContext from '../../../../../shared/context';
import { MENU_ITEMS } from './constants';

import './style.scss';

const EditCollectionModal = lazy(() =>
    import(/* webpackChunkName: 'EditCollectionModal' */ '../../../../../shared/components/EditCollectionModal')
);
const SocialSharingModal = lazy(() =>
    import(/* webpackChunkName: 'SocialSharingModal' */ '../../../../../shared/components/SocialSharingModal')
);
const DeleteConfirmationModal = lazy(() =>
    import(/* webpackChunkName: 'DeleteConfirmationModal' */ '../../../../../shared/components/DeleteConfirmationModal')
);

const { header } = EventTypes.collectionPage;

const Actions = ({ collection, updateCollection, socialShareCollection }) => {
    const [activeModal, setActiveModal] = useState(null);

    const { userId } = getContext();
    const { businessId } = useContext(SharedContext);

    const clearActiveModal = () => setActiveModal(null);

    const onCollectionDelete = async () => {
        await deleteCollection(collection.id);

        redirectTo(pathfinder('collections_lobby_my_lists'));
    };

    const onCollectionShare = () => socialShareCollection({ id: collection.id });

    const MODALS = {
        [CollectionActionType.SOCIAL_SHARE]: (
            <SocialSharingModal
                collection={collection}
                onCollectionShare={onCollectionShare}
                onClose={clearActiveModal}
            />
        ),
        [CollectionActionType.EDIT]: (
            <EditCollectionModal collection={collection} onSubmit={updateCollection} onClose={clearActiveModal} />
        ),
        [CollectionActionType.DELETE]: (
            <DeleteConfirmationModal
                id={collection.id}
                ownerType={collection.ownerType}
                onConfirm={onCollectionDelete}
                onClose={clearActiveModal}
            />
        ),
    };

    const onMenuClick = () => {
        eventsReporter.report(header.menu.clicked);
    };

    const onMenuItemClick = (menuItem) => {
        reportActionClick(menuItem);

        switch (menuItem) {
            case CollectionActionType.EDIT: {
                setActiveModal(CollectionActionType.EDIT);
                break;
            }
            case CollectionActionType.DELETE: {
                setActiveModal(CollectionActionType.DELETE);
                break;
            }
            default: {
                break;
            }
        }
    };

    const onSocialShareClick = () => {
        reportActionClick(CollectionActionType.SOCIAL_SHARE);

        setActiveModal(CollectionActionType.SOCIAL_SHARE);
    };

    const reportActionClick = (actionName) => {
        eventsReporter.report(header.actions[actionName].clicked, {
            actionName,
        });
    };

    const isOwner = userId === collection.creator.id;
    const isPersonal = collection.ownerType === OwnerType.USER;
    const isSharedWithTeam = businessId === collection.ownerId;
    const showMenu = isOwner || isSharedWithTeam;
    const showSocialSharing = isPersonal && isOwner;

    if (!showMenu && !showSocialSharing) {
        return null;
    }

    return (
        <section className="actions">
            {showSocialSharing && <ActionIcon onClick={onSocialShareClick} icon={s_share} />}
            {showMenu && <Menu menuItems={MENU_ITEMS} onClick={onMenuClick} onMenuItemClick={onMenuItemClick} />}
            {MODALS[activeModal] && <Suspense fallback={null}>{MODALS[activeModal]}</Suspense>}
        </section>
    );
};

Actions.propTypes = {
    collection: object,
    updateCollection: func,
    socialShareCollection: func,
};

const mapStateToProps = ({ collection }) => ({ collection });

const mapDispatchToProps = {
    updateCollection,
    socialShareCollection,
};

export { Actions };
export default connect(mapStateToProps, mapDispatchToProps)(Actions);
